import { Component, For, Show } from "solid-js"
import { useScenarioStore } from "../stores/scenario"
import { formatSimTime } from "../types"

const SPEED_OPTIONS = [
    { value: 1, label: "1x (realtid)" },
    { value: 2, label: "2x (standard)" },
    { value: 5, label: "5x" },
    { value: 10, label: "10x" },
    { value: 20, label: "20x" },
]

const SpeedPanel: Component = () => {
    const [scenario, scenarioActions] = useScenarioStore();
    
    const handleAdd = () => {
        const newControl = {
            id: `speed-${Date.now()}`,
            time: { hour: 23, minute: 0, second: 0 },
            speedMultiplier: 2,
        }
        scenarioActions.addSpeedControl()
        scenarioActions.scheduleSave()
    }

    const handleTimeChange = (id: string, field: "hour" | "minute" | "second", value: string) => {
        const control = scenario.speedControls.find((c) => c.id === id)
        if (!control) return

        // Allow empty string (user is typing)
        if (value === "") {
            scenarioActions.updateSpeedControl(id, { time: { ...control.time, [field]: 0 } })
            return
        }

        const num = parseInt(value, 10)
        if (isNaN(num)) return
        scenarioActions.updateSpeedControl(id, { time: { ...control.time, [field]: num } })
    }

    const handleSpeedChange = (id: string, value: string) => {
        const multiplier = parseFloat(value)
        if (isNaN(multiplier)) return
        scenarioActions.updateSpeedControl(id, { speedMultiplier: multiplier })
        scenarioActions.scheduleSave()
    }

    return (
        <>
            <div class="border-b border-gray-200 bg-white p-3">
                <h2 class="mb-2 text-sm font-semibold text-gray-700">Hastighetskontroll</h2>
                <p class="mb-2 text-[10px] text-gray-400">
                    Ändra uppspelningshastighet vid specifika tidpunkter. Standard är 2x om ingen hastighet anges.
                </p>
                <button
                    class="w-full rounded bg-blue-500 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-600"
                    onClick={handleAdd}
                >
                    + Lägg till hastighetskontroll
                </button>
            </div>

            <div class="flex-1 overflow-y-auto">
                <For each={scenario.speedControls}>
                    {(control) => (
                        <div class="space-y-1.5 border-b border-gray-200 px-3 py-2">
                            <div class="flex items-center justify-between">
                                <span class="font-mono text-xs text-gray-500">{formatSimTime(control.time)}</span>
                                <button
                                    class="text-xs text-red-400 hover:text-red-600"
                                    onClick={() => {
                                        scenarioActions.removeSpeedControl(control.id)
                                        scenarioActions.scheduleSave()
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            {/* Time inputs */}
                            <div class="flex gap-1">
                                <input
                                    type="number"
                                    min="0"
                                    max="23"
                                    value={control.time.hour}
                                    onInput={(e) => handleTimeChange(control.id, "hour", e.currentTarget.value)}
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
                                />
                                <span class="self-center text-xs text-gray-400">:</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={control.time.minute}
                                    onInput={(e) => handleTimeChange(control.id, "minute", e.currentTarget.value)}
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
                                />
                                <span class="self-center text-xs text-gray-400">:</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={control.time.second}
                                    onInput={(e) => handleTimeChange(control.id, "second", e.currentTarget.value)}
                                    onBlur={() => scenarioActions.scheduleSave()}
                                    class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
                                />
                            </div>{" "}
                            {/* Speed dropdown */}
                            <div class="flex items-center gap-2">
                                <label class="text-xs whitespace-nowrap text-gray-600">Hastighet:</label>
                                <select
                                    value={control.speedMultiplier}
                                    onChange={(e) => handleSpeedChange(control.id, e.currentTarget.value)}
                                    class="flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                                >
                                    <For each={SPEED_OPTIONS}>
                                        {(option) => <option value={option.value}>{option.label}</option>}
                                    </For>
                                </select>
                            </div>
                        </div>
                    )}
                </For>

                <Show when={scenario.speedControls.length === 0}>
                    <div class="p-4 text-center text-sm text-gray-400">
                        Inga hastighetskontroller ännu. Standard är 2x uppspelning.
                    </div>
                </Show>
            </div>
        </>
    )
}

export default SpeedPanel
